import { ChevronRight, Phone, Mail, MessageSquare, Linkedin, Twitter, Database, ArrowRight, CircleDot, TerminalSquare, Network, Cpu, WifiIcon, ShieldCheck, LineChart, Gauge, Target, Bot, Users, CalendarCheck, Zap, BrainCircuit, Activity, Briefcase, CheckCircle, Clock, DollarSign, Search, TrendingUp, AlertTriangle, X, BarChart3,  Globe, TestTube2 } from 'lucide-react'; // Added more icons: BarChart3, TestTubeDiagonal, Globe
import React, { useState, useEffect, useRef } from 'react';

// --- Calendly Link ---
const CALENDLY_URL = 'https://calendly.com/manas-o4af/30min';

// --- Reusable Utility Function ---
const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
        // Adjust for fixed header height
        const headerHeight = document.querySelector('header')?.offsetHeight || 0;
        // Banner is now below header, so no need to account for it here
        const offsetTop = element.getBoundingClientRect().top + window.pageYOffset - headerHeight;

        window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
};

// --- NEW: Announcement Banner Component (Positioned below Header) ---
const AnnouncementBanner = () => {
    const [isVisible, setIsVisible] = useState(true);
    const [headerHeight, setHeaderHeight] = useState(0);

    // Get header height to position banner correctly
    useEffect(() => {
        const headerElement = document.querySelector('header');
        const updateHeaderHeight = () => {
            if (headerElement) {
                setHeaderHeight(headerElement.offsetHeight);
            }
        };

        updateHeaderHeight(); // Initial check
        const resizeObserver = new ResizeObserver(updateHeaderHeight);
        if (headerElement) {
            resizeObserver.observe(headerElement);
        }

        // Also listen for window resize as header might reflow
        window.addEventListener('resize', updateHeaderHeight);

        return () => {
            if (headerElement) {
                resizeObserver.unobserve(headerElement);
            }
            window.removeEventListener('resize', updateHeaderHeight);
        };
    }, []);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            setIsVisible(false);
        }
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div
            id="announcement-banner"
            className="bg-gradient-to-r from-cyan-800/80 via-cyan-600/70 to-cyan-800/80 text-cyan-100 text-sm p-3 text-center flex items-center justify-center fixed w-full z-40 border-b border-cyan-700/30 backdrop-blur-sm transition-all duration-300"
            style={{ top: `${headerHeight}px` }} // Position below header
        >
            <AlertTriangle size={18} className="mr-3 text-cyan-300 flex-shrink-0" />
            <span className="flex-grow">
                Important Update: <strong className="font-semibold text-white">ANNE AI SDR</strong> will be replacing EchidnaMail starting July 2025. Prepare for enhanced capabilities!
            </span>
             <div
                role="button"
                tabIndex={0}
                onClick={() => setIsVisible(false)} // Keep close functionality
                onKeyDown={handleKeyDown} // Keep close functionality
                className="ml-4 p-1 rounded-md hover:bg-cyan-500/20 text-cyan-300 hover:text-white transition-colors flex-shrink-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-opacity-50"
                aria-label="Dismiss announcement"
            >
                <X size={16} />
            </div>
        </div>
    );
};


// --- Header Component (Uses div for buttons) ---
const Header = ({ activeSection }) => {
    const [currentSection, setCurrentSection] = useState('home');
    const headerRef = useRef(null); // Ref to get header height for banner positioning

    // Internal Navigation Handler - Keep as is
    const handleNavClick = (sectionId) => {
        setCurrentSection(sectionId);
        scrollToSection(sectionId);
    };

    // Helper for keyboard activation (Internal Nav) - Keep as is
    const handleKeyDown = (event, sectionId) => {
        if (event.key === 'Enter' || event.key === ' ') {
             handleNavClick(sectionId);
        }
    };

    // External Link Handler
    const redirectToCalendly = () => {
        window.location.href = CALENDLY_URL;
    };

    // Keyboard handler for external link
    const handleExternalLinkKeyDown = (event) => {
         if (event.key === 'Enter' || event.key === ' ') {
            redirectToCalendly();
        }
    }

    return (
        <header ref={headerRef} className="border-b border-cyan-900/30 backdrop-blur-sm bg-black/70 fixed w-full z-50 top-0 left-0">
            <div className="container mx-auto flex justify-between items-center py-6 px-4">
                 {/* Logo - Keep internal scroll */}
                <div className="flex items-center cursor-pointer" onClick={() => handleNavClick('home')} onKeyDown={(e) => handleKeyDown(e, 'home')} tabIndex="0">
                    <div className="text-cyan-400 font-bold text-2xl tracking-wider mr-2">ANNE</div>
                    <div className="text-xs bg-cyan-900/30 px-2 py-1 rounded-sm">AI SDR v2.0</div>
                </div>
                {/* Navigation Links - Keep internal scroll */}
                <nav className="hidden md:flex space-x-8">
                     <div role="button" tabIndex="0" onClick={() => handleNavClick('home')} onKeyDown={(e) => handleKeyDown(e, 'home')} className={`${activeSection === 'home' ? 'text-cyan-400' : 'text-gray-400'} hover:text-cyan-300 transition text-sm font-medium tracking-wide cursor-pointer focus:outline-none focus:ring-1 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-black rounded px-1`}>HOME</div>
                     <div role="button" tabIndex="0" onClick={() => handleNavClick('features')} onKeyDown={(e) => handleKeyDown(e, 'features')} className={`${activeSection === 'features' ? 'text-cyan-400' : 'text-gray-400'} hover:text-cyan-300 transition text-sm font-medium tracking-wide cursor-pointer focus:outline-none focus:ring-1 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-black rounded px-1`}>FEATURES</div>
                     <div role="button" tabIndex="0" onClick={() => handleNavClick('technology')} onKeyDown={(e) => handleKeyDown(e, 'technology')} className={`${activeSection === 'technology' ? 'text-cyan-400' : 'text-gray-400'} hover:text-cyan-300 transition text-sm font-medium tracking-wide cursor-pointer focus:outline-none focus:ring-1 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-black rounded px-1`}>TECHNOLOGY</div>
                     <div role="button" tabIndex="0" onClick={() => handleNavClick('cta')} onKeyDown={(e) => handleKeyDown(e, 'cta')} className={`${activeSection === 'cta' ? 'text-cyan-400' : 'text-gray-400'} hover:text-cyan-300 transition text-sm font-medium tracking-wide cursor-pointer focus:outline-none focus:ring-1 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-black rounded px-1`}>PRICING</div>
                </nav>
                 {/* Request Demo Button - Redirect to Calendly */}
                 <div role="button" tabIndex="0" onClick={redirectToCalendly} onKeyDown={handleExternalLinkKeyDown} className="bg-cyan-900/30 border border-cyan-400/30 hover:bg-cyan-800/40 text-cyan-400 py-2 px-6 rounded-sm flex items-center text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-black">
                    REQUEST DEMO
                    <ChevronRight size={16} className="ml-1" />
                </div>
            </div>
        </header>
    );
};


// --- Hero Component (Uses div for buttons) ---
const Hero = () => {

     // External Link Handler
    const redirectToCalendly = () => {
        window.location.href = CALENDLY_URL;
    };

     // Helper for keyboard activation for external links
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            redirectToCalendly();
        }
    };

    return (
        <section id="home" className="pt-40 pb-24 px-4 relative isolate overflow-hidden">
            {/* Adjust pt dynamically based on header+banner height in main component */}
            <div className="container mx-auto relative z-10">
                <div className="flex flex-col lg:flex-row items-center">
                    {/* Text Content */}
                    <div className="lg:w-7/12 xl:w-6/12">
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-white mb-6 tracking-tight leading-tight glitch-text">
                            <span className="text-cyan-400">ANNE</span>: Sales Development <br />Redefined By AI
                        </h1>
                        <div className="w-24 h-1 bg-cyan-400 mb-8"></div>
                        <p className="text-lg md:text-xl text-gray-400 max-w-xl mb-10 leading-relaxed">
                            She appears human. She learns. She adapts. Anne conducts outreach across all channels with autonomous precision and human-like empathy.
                        </p>
                        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6 mb-16">
                             {/* Activate Anne Button - Redirect to Calendly */}
                             <div
                                role="button"
                                tabIndex={0}
                                onClick={redirectToCalendly}
                                onKeyDown={handleKeyDown}
                                className="bg-cyan-600 hover:bg-cyan-500 text-white py-3 px-8 rounded-sm flex items-center justify-center shadow-lg shadow-cyan-500/20 transition duration-300 transform hover:scale-105 cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 focus:ring-offset-black"
                             >
                                ACTIVATE ANNE
                                <ArrowRight size={18} className="ml-2" />
                            </div>
                             {/* View Documentation Button - Redirect to Calendly */}
                             <div
                                role="button"
                                tabIndex={0}
                                onClick={redirectToCalendly}
                                onKeyDown={handleKeyDown}
                                className="border border-cyan-700/50 hover:border-cyan-400 text-cyan-400 hover:text-cyan-300 py-3 px-8 rounded-sm transition duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-black flex items-center justify-center" // Added flex centering
                             >
                                VIEW DOCUMENTATION
                            </div>
                        </div>
                        <div className="flex items-center">
                            <div className="text-xs uppercase text-gray-500 tracking-wider mr-4">Trusted by</div>
                            <div className="flex space-x-8">
                                <div className="text-gray-500 hover:text-gray-300 transition font-light text-lg cursor-default">CYBERLIFE</div>
                                <div className="text-gray-500 hover:text-gray-300 transition font-light text-lg cursor-default">KAMSKI INC</div>
                                <div className="text-gray-500 hover:text-gray-300 transition font-light text-lg cursor-default">STRATFORD TOWER</div>
                            </div>
                        </div>
                    </div>

                    {/* Image - Modified positioning to be 20% right from middle */}
                    <div className="lg:w-5/12 xl:w-6/12 mt-16 lg:mt-0 flex justify-center lg:justify-start relative">
                        <div className="relative w-full">
                            <img
                                src="https://cybverlake-test.s3.ap-south-1.amazonaws.com/ChatGPT_Image_Apr_26__2025__11_39_08_AM-removebg-preview.png"
                                alt="Anne AI Sales Development Representative Interface"
                                className="max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl h-auto glow-effect opacity-90 lg:absolute lg:left-1/5 lg:top-1/2 lg:-translate-y-1/2 lg:ml-16"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};


// --- Feature Card Component (No changes needed) ---
const FeatureCard = ({ icon, title, description }) => {
    return (
        <div className="border border-cyan-900/40 bg-black/40 p-6 rounded-lg hover:border-cyan-700/60 transition group backdrop-blur-sm hover:shadow-lg hover:shadow-cyan-900/30">
            <div className="flex items-center mb-4">
                {icon}
                <h3 className="text-xl font-medium ml-3 text-gray-100">{title}</h3>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
            <div className="mt-5 h-px bg-cyan-900/50 relative">
                <div className="absolute left-0 top-0 h-px w-0 group-hover:w-1/3 bg-cyan-500 transition-all duration-400"></div>
            </div>
        </div>
    );
};

// --- Features Section Component (Added More Features) ---
const FeaturesSection = () => {
    const features = [
        { icon: <Phone className="text-cyan-400" size={22} />, title: "Autonomous Cold Calls", description: "Human-like conversational AI handles outreach calls, qualifies leads, and sets meetings." },
        { icon: <Mail className="text-cyan-400" size={22} />, title: "Adaptive Cold Emails", description: "Hyper-personalized sequences that learn and adapt based on real-time engagement data." },
        { icon: <MessageSquare className="text-cyan-400" size={22} />, title: "Conversational SMS", description: "Engage prospects through direct, natural language text messages for quicker responses." },
        { icon: <Linkedin className="text-cyan-400" size={22} />, title: "Stealth LinkedIn Outreach", description: "Automated connection requests and personalized messaging designed to bypass detection." },
        { icon: <Twitter className="text-cyan-400" size={22} />, title: "Targeted Twitter DMs", description: "Leverage social selling with intelligent, context-aware direct messages on Twitter." },
        { icon: <Database className="text-cyan-400" size={22} />, title: "Integrated CRM & Learning", description: "Built-in CRM syncs data, tracks interactions, and fuels Anne's continuous learning loop." },
        { icon: <Target className="text-cyan-400" size={22} />, title: "AI-Powered SEO Insights", description: "Analyzes prospect websites and market trends to suggest relevant talking points and identify SEO opportunities." },
        { icon: <BarChart3 className="text-cyan-400" size={22} />, title: "Real-time Analytics", description: "Monitor campaign performance, engagement rates, and meeting metrics through a live dashboard." },
        { icon: <TestTube2 className="text-cyan-400" size={22} />, title: "A/B Testing Engine", description: "Automatically test different messaging, subject lines, and call scripts to optimize results." }, // Corrected icon name
    ];
    return (
        <section id="features" className="py-24 bg-gray-900/30 border-y border-cyan-900/30 px-4 isolate">
            <div className="container mx-auto">
                <div className="text-center mb-16">
                    <div className="uppercase text-xs tracking-wider text-cyan-400 mb-2 font-semibold">Core Capabilities</div>
                    <h2 className="text-4xl lg:text-5xl font-light mb-4 text-white">ANNE's Operational Matrix</h2>
                    <p className="text-gray-400 max-w-xl mx-auto text-lg">
                        Anne autonomously executes the full spectrum of SDR tasks, learning and optimizing with every interaction.
                    </p>
                </div>
                 {/* Adjusted grid for 9 items */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {features.map((feature, index) => (
                        <FeatureCard key={index} icon={feature.icon} title={feature.title} description={feature.description}/>
                    ))}
                </div>
            </div>
        </section>
    );
};

// --- Interactive Components (Terminal, HolographicStat, NeuralNetwork, MarketAnalysis, VectorDatabase remain the same) ---
const TerminalDemo = () => { /* ... code remains the same ... */
    const [terminalText, setTerminalText] = useState('');
    const fullText = '> Initializing ANNE_CORE v2.0...\n> Authenticating secure connection...\n> Loading NLP model: ATLAS-7b...\n> Syncing CRM data [||||||||||] 100%\n> Activating multi-channel outreach protocols...\n> Vocal synthesis engine online.\n> System Status: OPERATIONAL\n> Analyzing target market: Tech Sector (EMEA)...\n> Identifying key competitors...\n> Waiting for instructions...\n';
    const terminalRef = useRef(null);
    useEffect(() => {
        let index = 0;
        const interval = setInterval(() => {
            if (index < fullText.length) {
                setTerminalText(current => current + fullText.charAt(index));
                index++;
                if (terminalRef.current) terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
            } else { clearInterval(interval); }
        }, 35);
        return () => clearInterval(interval);
    }, []);
    return (
        <div className="bg-black/50 border border-cyan-900/50 p-6 rounded-lg group hover:border-cyan-600/70 transition-all duration-300 shadow-lg shadow-cyan-900/10 backdrop-blur-sm h-full flex flex-col">
             <div className="flex items-center mb-4 flex-shrink-0"> <TerminalSquare className="text-cyan-400" size={20} /> <h3 className="text-lg text-gray-300 ml-2 font-medium">System Console</h3> <div className="flex ml-auto space-x-2"> <div className="w-3 h-3 rounded-full bg-yellow-600/70 group-hover:bg-yellow-500 transition"></div> <div className="w-3 h-3 rounded-full bg-green-600/70 group-hover:bg-green-500 transition"></div> <div className="w-3 h-3 rounded-full bg-red-600/70 group-hover:bg-red-500 transition"></div> </div> </div>
            <div ref={terminalRef} className="bg-gray-900/80 text-green-400 font-mono text-xs p-4 rounded-md flex-grow overflow-y-auto custom-scrollbar border border-gray-700/50 min-h-[200px]">
                <pre className="whitespace-pre-wrap break-words">{terminalText}</pre>
                <div className="inline-block h-3 w-2 bg-green-400 animate-pulse ml-1 align-bottom"></div>
            </div>
        </div>
    );
 };
const HolographicStat = () => { /* ... code remains the same ... */
    const [hovering, setHovering] = useState(false);
    const stats = [{ label: "Response Rate", value: 93, unit: "%", width: "93%" }, { label: "Meetings Booked (MTD)", value: 147, unit: "", width: "74%" }, { label: "Positive Sentiment", value: 88, unit: "%", width: "88%" }, { label: "Avg. Handle Time", value: 4.2, unit: " min", width: "58%" },];
    return (
        <div className="relative p-6 border border-cyan-900/60 rounded-lg overflow-hidden group bg-black/50 backdrop-blur-sm hover:border-cyan-600/80 transition-all duration-300 shadow-lg shadow-cyan-900/10 h-full" onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
            <div className={`absolute inset-0 bg-gradient-to-br from-cyan-900/10 to-blue-900/5 transition-opacity duration-500 ${hovering ? 'opacity-100' : 'opacity-50'}`}></div> <div className={`absolute inset-0 bg-grid-pattern transition-opacity duration-500 ${hovering ? 'opacity-20' : 'opacity-5'}`}></div>
            <div className="relative z-10">
                 <h3 className="text-lg text-cyan-400 mb-6 flex items-center font-medium"> <Gauge className="mr-2" size={20} /> Real-time Performance </h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                    {stats.map((stat, index) => ( <div key={index}> <div className="text-3xl font-light text-white mb-1"> {stat.value}<span className="text-lg text-cyan-400/80">{stat.unit}</span> </div> <div className="text-gray-400 text-xs uppercase tracking-wider">{stat.label}</div> <div className="h-1.5 w-full bg-cyan-900/30 rounded-full mt-2 overflow-hidden"> <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all duration-700 ease-out" style={{ width: stat.width }}></div> </div> </div> ))}
                </div>
            </div>
             <div className={`absolute inset-0 border border-cyan-600/0 group-hover:border-cyan-600/50 rounded-lg transition-all duration-300 pointer-events-none`}></div> <div className={`absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 bg-cyan-500/10 blur-3xl transition-opacity duration-500 ${hovering ? 'opacity-100' : 'opacity-0'} rounded-full`}></div>
        </div>
    );
 };
const NeuralNetworkAnimation = () => { /* ... code remains the same ... */
    return (
        <div className="relative border border-cyan-900/50 p-6 rounded-lg overflow-hidden bg-black/50 group hover:border-cyan-600/70 transition-all duration-300 shadow-lg shadow-cyan-900/10 backdrop-blur-sm h-full flex flex-col">
             <h3 className="text-lg text-gray-300 mb-4 flex items-center font-medium flex-shrink-0"> <BrainCircuit className="text-cyan-400 mr-2" size={20} /> Cognitive Core Visualization </h3>
            <div className="flex-grow relative min-h-[250px]">
                <svg className="w-full h-full" viewBox="0 0 300 200" preserveAspectRatio="xMidYMid meet">
                     <g className="nodes"> <circle cx="50" cy="40" r="5" fill="#0891b2" className="animate-pulse-slow" /> <circle cx="50" cy="80" r="5" fill="#0891b2" className="animate-pulse-slow-delay-1" /> <circle cx="50" cy="120" r="5" fill="#0891b2" className="animate-pulse-slow-delay-2" /> <circle cx="50" cy="160" r="5" fill="#0891b2" className="animate-pulse-slow-delay-3" /> <circle cx="125" cy="50" r="5" fill="#0e7490" className="animate-pulse-slow-delay-2" /> <circle cx="125" cy="90" r="5" fill="#0e7490" className="animate-pulse-slow-delay-3" /> <circle cx="125" cy="130" r="5" fill="#0e7490" className="animate-pulse-slow-delay-1" /> <circle cx="125" cy="170" r="5" fill="#0e7490" className="animate-pulse-slow" /> <circle cx="200" cy="70" r="5" fill="#155e75" className="animate-pulse-slow-delay-1" /> <circle cx="200" cy="110" r="5" fill="#155e75" className="animate-pulse-slow-delay-2" /> <circle cx="200" cy="150" r="5" fill="#155e75" className="animate-pulse-slow-delay-3" /> <circle cx="275" cy="90" r="5" fill="#06b6d4" className="animate-pulse-slow-delay-3" /> <circle cx="275" cy="130" r="5" fill="#06b6d4" className="animate-pulse-slow-delay-2" /> </g>
                     <g className="connections" stroke="#0e7490" strokeWidth="0.5" opacity="0.4" > <line x1="50" y1="40" x2="125" y2="50" className="neural-line animate-neural-1" /> <line x1="50" y1="40" x2="125" y2="90" className="neural-line animate-neural-2" /> <line x1="50" y1="40" x2="125" y2="130" className="neural-line animate-neural-3" /> <line x1="50" y1="40" x2="125" y2="170" className="neural-line animate-neural-1" /> <line x1="50" y1="80" x2="125" y2="50" className="neural-line animate-neural-2" /> <line x1="50" y1="80" x2="125" y2="90" className="neural-line animate-neural-3" /> <line x1="50" y1="80" x2="125" y2="130" className="neural-line animate-neural-1" /> <line x1="50" y1="80" x2="125" y2="170" className="neural-line animate-neural-2" /> <line x1="50" y1="120" x2="125" y2="50" className="neural-line animate-neural-3" /> <line x1="50" y1="120" x2="125" y2="90" className="neural-line animate-neural-1" /> <line x1="50" y1="120" x2="125" y2="130" className="neural-line animate-neural-2" /> <line x1="50" y1="120" x2="125" y2="170" className="neural-line animate-neural-3" /> <line x1="50" y1="160" x2="125" y2="50" className="neural-line animate-neural-1" /> <line x1="50" y1="160" x2="125" y2="90" className="neural-line animate-neural-2" /> <line x1="50" y1="160" x2="125" y2="130" className="neural-line animate-neural-3" /> <line x1="50" y1="160" x2="125" y2="170" className="neural-line animate-neural-1" /> <line x1="125" y1="50" x2="200" y2="70" className="neural-line animate-neural-2" /> <line x1="125" y1="50" x2="200" y2="110" className="neural-line animate-neural-3" /> <line x1="125" y1="50" x2="200" y2="150" className="neural-line animate-neural-1" /> <line x1="125" y1="90" x2="200" y2="70" className="neural-line animate-neural-2" /> <line x1="125" y1="90" x2="200" y2="110" className="neural-line animate-neural-3" /> <line x1="125" y1="90" x2="200" y2="150" className="neural-line animate-neural-1" /> <line x1="125" y1="130" x2="200" y2="70" className="neural-line animate-neural-2" /> <line x1="125" y1="130" x2="200" y2="110" className="neural-line animate-neural-3" /> <line x1="125" y1="130" x2="200" y2="150" className="neural-line animate-neural-1" /> <line x1="125" y1="170" x2="200" y2="70" className="neural-line animate-neural-2" /> <line x1="125" y1="170" x2="200" y2="110" className="neural-line animate-neural-3" /> <line x1="125" y1="170" x2="200" y2="150" className="neural-line animate-neural-1" /> <line x1="200" y1="70" x2="275" y2="90" className="neural-line animate-neural-2" /> <line x1="200" y1="70" x2="275" y2="130" className="neural-line animate-neural-3" /> <line x1="200" y1="110" x2="275" y2="90" className="neural-line animate-neural-1" /> <line x1="200" y1="110" x2="275" y2="130" className="neural-line animate-neural-2" /> <line x1="200" y1="150" x2="275" y2="90" className="neural-line animate-neural-3" /> <line x1="200" y1="150" x2="275" y2="130" className="neural-line animate-neural-1" /> </g>
                </svg>
                 <div className="absolute bottom-3 right-3 text-xs text-cyan-400/70 bg-black/60 px-2 py-1 rounded"> NLP Model: ATLAS-7b </div>
            </div>
        </div>
    );
};
const MarketAnalysisPanel = () => { /* ... code remains the same ... */
    const [analysisPhase, setAnalysisPhase] = useState(0);
    const phases = ["Identifying Trends", "Competitor Mapping", "Opportunity Scoring", "Market Segmentation"];
    useEffect(() => { const interval = setInterval(() => { setAnalysisPhase(current => (current + 1) % phases.length); }, 2500); return () => clearInterval(interval); }, [phases.length]);
    const competitorData = [{ name: "Comp A", score: 75, x: 40, y: 40 }, { name: "Comp B", score: 60, x: 70, y: 60 }, { name: "Comp C", score: 85, x: 50, y: 80 }, { name: "You", score: 90, x: 80, y: 30 },];
    return (
        <div className="bg-black/50 border border-cyan-900/50 p-6 rounded-lg relative overflow-hidden group hover:border-cyan-600/70 transition-all duration-300 shadow-lg shadow-cyan-900/10 backdrop-blur-sm h-full flex flex-col">
            <h3 className="text-lg text-gray-300 mb-4 flex items-center font-medium flex-shrink-0"> <Target className="text-cyan-400 mr-2" size={20} /> Market & Competitor Analysis </h3>
            <div className="flex-grow flex flex-col items-center justify-center p-4 relative">
                <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none"> <circle cx="50" cy="50" r="45" fill="none" stroke="#0e7490" strokeWidth="0.5"/> <circle cx="50" cy="50" r="30" fill="none" stroke="#0e7490" strokeWidth="0.5"/> <circle cx="50" cy="50" r="15" fill="none" stroke="#0e7490" strokeWidth="0.5"/> <line x1="50" y1="5" x2="50" y2="95" stroke="#0e7490" strokeWidth="0.5"/> <line x1="5" y1="50" x2="95" y2="50" stroke="#0e7490" strokeWidth="0.5"/> <line x1="15" y1="15" x2="85" y2="85" stroke="#0e7490" strokeWidth="0.5"/> <line x1="15" y1="85" x2="85" y2="15" stroke="#0e7490" strokeWidth="0.5"/> </svg>
                <svg className="relative w-40 h-40" viewBox="0 0 100 100"> {competitorData.map((comp, index) => ( <g key={comp.name} className="transition-transform duration-500 ease-out"> <circle cx={comp.x} cy={comp.y} r={comp.name === "You" ? 5 : 4} fill={comp.name === "You" ? "#06b6d4" : "#155e75"} className={comp.name === "You" ? "animate-pulse" : ""} opacity={0.8} /> <text x={comp.x} y={comp.y - 6} fontSize="6" fill={comp.name === "You" ? "#67e8f9" : "#64748b"} textAnchor="middle" className="font-mono"> {comp.name} </text> </g> ))} <line x1="50" y1="50" x2="50" y2="5" stroke="#38bdf8" strokeWidth="1" className="origin-center animate-spin-slow" strokeLinecap="round" opacity="0.6" /> </svg>
                <div className="mt-4 text-center h-6"> <p className="text-cyan-400 text-sm animate-pulse">{phases[analysisPhase]}...</p> </div>
            </div>
             {/* Using div styled as button - Informational only, no redirect */}
            <div className="flex justify-center mt-2 flex-shrink-0">
                <div className={`px-4 py-1.5 rounded-md text-xs font-medium flex items-center bg-cyan-900/30 text-cyan-400 border border-cyan-700/50 cursor-default`}>
                     <Zap size={12} className="mr-1.5"/> AUTO-ANALYSIS ACTIVE
                 </div>
             </div>
        </div>
    );
};
const VectorDatabaseMonitor = () => { /* ... code remains the same ... */
    const [activeShard, setActiveShard] = useState(0);
    const shardCount = 8;
    useEffect(() => { const interval = setInterval(() => { setActiveShard(current => (current + Math.floor(Math.random() * 3) + 1) % shardCount); }, 900); return () => clearInterval(interval); }, [shardCount]);
    const queryLatency = 15 + Math.sin(Date.now() / 800) * 5; const indexSize = 12.5 + Math.cos(Date.now() / 2000) * 0.5; const vectorCount = 2.1;
    return (
        <div className="bg-black/50 border border-cyan-900/50 p-6 rounded-lg relative overflow-hidden group hover:border-cyan-600/70 transition-all duration-300 shadow-lg shadow-cyan-900/10 backdrop-blur-sm h-full flex flex-col">
             <h3 className="text-lg text-gray-300 mb-4 flex items-center font-medium flex-shrink-0"> <Database className="text-cyan-400 mr-2" size={20} /> Vector Database Operations </h3>
            <div className="grid grid-cols-4 gap-3 mb-5 flex-grow items-center justify-center min-h-[120px]">
                {Array.from({ length: shardCount }, (_, i) => ( <div key={i} className={`bg-gray-900/60 h-10 rounded flex items-center justify-center border relative overflow-hidden transition-all duration-300 ${i === activeShard ? 'border-cyan-500 scale-105 shadow-sm shadow-cyan-500/30' : 'border-gray-700/50 group-hover:border-cyan-900/50'}`}> <span className="text-[10px] text-gray-500 font-mono z-10">{`S${i + 1}`}</span> <div className={`absolute top-1 right-1 w-1.5 h-1.5 rounded-full transition-colors duration-300 ${i === activeShard ? 'bg-cyan-400 animate-pulse' : 'bg-gray-600'}`}></div> {i === activeShard && ( <div className="absolute bottom-0 left-0 w-full h-1/4 bg-gradient-to-t from-cyan-600/50 to-transparent animate-pulse-slow"></div> )} </div> ))}
            </div>
            <div className="flex items-center justify-between text-xs text-gray-400 font-mono border-t border-cyan-900/30 pt-3 flex-shrink-0"> <div>Latency: {queryLatency.toFixed(0)}ms</div> <div>Index: {indexSize.toFixed(1)}TB</div> <div>Vectors: {vectorCount.toFixed(1)}B</div> <div><span className="text-green-400">●</span> Healthy</div> </div>
        </div>
    );
};

// --- RealTimeChartSimulation Component (Less "Squished") ---
const RealTimeChartSimulation = () => { // No external links here
    const MAX_POINTS = 15;
    const DATA_MIN = 20;
    const DATA_MAX = 80;
    const DATA_RANGE = DATA_MAX - DATA_MIN;

    const generateInitialData = () => Array.from({ length: MAX_POINTS }, () => Math.random() * DATA_RANGE + DATA_MIN);
    const [data, setData] = useState(generateInitialData());

    useEffect(() => {
        const interval = setInterval(() => {
            setData(currentData => {
                const lastValue = currentData[currentData.length - 1];
                let change = (Math.random() * 10 - 5);
                let newValue = lastValue + change;
                newValue = Math.max(DATA_MIN - 5, Math.min(DATA_MAX + 5, newValue));
                if (newValue < DATA_MIN) newValue += Math.random() * 3;
                if (newValue > DATA_MAX) newValue -= Math.random() * 3;

                const newData = [...currentData.slice(1), newValue];
                return newData;
            });
        }, 1200);
        return () => clearInterval(interval);
    }, []);

    const SVG_HEIGHT = 140;
    const SVG_WIDTH = 400;
    const PADDING_Y = 20;
    const PADDING_X = 15;

    const scaleY = (value) => {
        const normValue = Math.max(0, Math.min(1, (value - (DATA_MIN - 5)) / (DATA_RANGE + 10)));
        return SVG_HEIGHT - (normValue * (SVG_HEIGHT - 2 * PADDING_Y) + PADDING_Y);
    };

    const chartWidth = SVG_WIDTH - (2 * PADDING_X);
    const segmentWidth = chartWidth / (MAX_POINTS - 1);
    const points = data.map((value, index) => `${PADDING_X + index * segmentWidth},${scaleY(value)}`).join(' ');
    const areaPoints = `M${PADDING_X},${SVG_HEIGHT} ${points} L${SVG_WIDTH - PADDING_X},${SVG_HEIGHT} Z`;
    const lastValue = data[data.length - 1];
    const targetValueY = scaleY(70);

    return (
        <div className="bg-black/40 border border-cyan-900/40 p-6 rounded-lg relative overflow-hidden group hover:border-cyan-600/60 transition-all duration-500 shadow-lg shadow-cyan-900/20 backdrop-blur-md h-full flex flex-col">
            <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-cyan-900/10 rounded-full blur-3xl pointer-events-none"></div>
            <h3 className="text-lg text-gray-200 mb-6 flex items-center font-light tracking-wide flex-shrink-0 group-hover:text-cyan-200 transition-colors duration-300">
                <LineChart className="text-cyan-400 mr-3" size={22} />
                Engagement Index
                <span className="ml-2 text-xs py-1 px-2 bg-cyan-900/40 rounded-full text-cyan-400/90 font-normal">LIVE</span>
            </h3>
            <div className="flex-grow w-full relative min-h-[260px]">
                <svg viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`} className="w-full h-full" preserveAspectRatio="none">
                    {[25, 50, 75].map(yValue => (
                        <line
                            key={yValue}
                            x1={PADDING_X}
                            y1={scaleY(yValue)}
                            x2={SVG_WIDTH - PADDING_X}
                            y2={scaleY(yValue)}
                            stroke="#1e293b"
                            strokeWidth="0.5"
                            strokeDasharray="2,3"
                            opacity="0.6"
                        />
                    ))}
                    {[25, 50, 75].map(yValue => (
                        <text
                            key={`label-${yValue}`}
                            x={PADDING_X - 10}
                            y={scaleY(yValue) + 3}
                            fontSize="8"
                            fill="#64748b"
                            textAnchor="end"
                        >
                            {yValue}
                        </text>
                    ))}
                    <line
                        x1={PADDING_X}
                        y1={targetValueY}
                        x2={SVG_WIDTH - PADDING_X}
                        y2={targetValueY}
                        stroke="#f59e0b"
                        strokeWidth="0.7"
                        strokeDasharray="4,5"
                        opacity="0.7"
                    />
                    <text
                        x={PADDING_X + 5}
                        y={targetValueY - 4}
                        fontSize="8"
                        fill="#f59e0b"
                        opacity="0.9"
                    >
                        Target (70)
                    </text>
                    <defs>
                        <linearGradient id="chart-area-gradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.3" />
                            <stop offset="50%" stopColor="#0891b2" stopOpacity="0.15" />
                            <stop offset="100%" stopColor="#082f49" stopOpacity="0.02" />
                        </linearGradient>
                        <linearGradient id="chart-line-gradient" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#22d3ee" />
                            <stop offset="50%" stopColor="#06b6d4" />
                            <stop offset="100%" stopColor="#0e7490" />
                        </linearGradient>
                        <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                            <feGaussianBlur stdDeviation="2" result="blur" />
                            <feComposite in="SourceGraphic" in2="blur" operator="over" />
                        </filter>
                    </defs>
                    <path d={areaPoints} fill="url(#chart-area-gradient)" />
                    <polyline
                        points={points}
                        fill="none"
                        stroke="url(#chart-line-gradient)"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        filter="url(#glow)"
                    />
                    {data.map((value, index) => (
                        <circle
                            key={`point-${index}`}
                            cx={PADDING_X + index * segmentWidth}
                            cy={scaleY(value)}
                            r="1.5"
                            fill="#0e7490"
                            opacity={index === data.length - 1 ? 0 : 0.6}
                        />
                    ))}
                    <circle
                        cx={PADDING_X + (MAX_POINTS - 1) * segmentWidth}
                        cy={scaleY(lastValue)}
                        r="4"
                        fill="#06b6d4"
                        stroke="#0f172a"
                        strokeWidth="1.5"
                        className="animate-pulse"
                    />
                    <text
                        x={PADDING_X + (MAX_POINTS - 1) * segmentWidth}
                        y={scaleY(lastValue) - 10}
                        fontSize="12"
                        fill="#06b6d4"
                        textAnchor="middle"
                        fontWeight="bold"
                        className="drop-shadow-md"
                    >
                        {Math.round(lastValue)}%
                    </text>
                </svg>
                <div className="absolute bottom-3 left-3 text-xs text-cyan-400/80 bg-black/70 px-3 py-1 rounded-full flex items-center space-x-1 border border-cyan-900/40">
                    <span className="h-1.5 w-1.5 bg-cyan-400 rounded-full animate-pulse block"></span>
                    <span>Real-time Analysis</span>
                </div>
            </div>
        </div>
    );
};


// --- Detailed Workflow Visualization Component (Sub-components remain same, uses div for buttons) ---
// Sub-components for displaying different step types
const EmailDisplay = ({ data }) => ( /* ... code remains the same ... */
    <div className="border border-cyan-900/50 rounded-md p-4 bg-gray-900/30 h-full text-left">
        <div className="flex justify-between items-center mb-3 pb-2 border-b border-cyan-900/30">
             <span className="text-xs text-cyan-400 font-medium">EMAIL SENT</span>
             <Mail size={16} className="text-cyan-600"/>
        </div>
        <p className="text-xs text-gray-500 mb-1">To: <span className="text-gray-300">{data.to}</span></p>
        <p className="text-xs text-gray-500 mb-2">Subject: <span className="text-gray-300">{data.subject}</span></p>
        <p className="text-sm text-gray-400 leading-relaxed line-clamp-3">{data.bodySnippet}</p>
    </div>
);
const LinkedInDisplay = ({ data }) => ( /* ... code remains the same ... */
     <div className="border border-cyan-900/50 rounded-md p-4 bg-gray-900/30 h-full text-left">
         <div className="flex justify-between items-center mb-3 pb-2 border-b border-cyan-900/30">
             <span className="text-xs text-cyan-400 font-medium">LINKEDIN MESSAGE</span>
             <Linkedin size={16} className="text-cyan-600"/>
         </div>
        <p className="text-xs text-gray-500 mb-2">To: <span className="text-gray-300">{data.to}</span></p>
        {/* Simple chat bubble simulation */}
        <div className="space-y-2">
            <div className="bg-cyan-800/50 p-2 rounded-lg rounded-bl-none">
                <p className="text-sm text-gray-200">{data.messageSnippet}</p>
            </div>
            {data.responseSnippet && (
                 <div className="bg-gray-700/50 p-2 rounded-lg rounded-br-none ml-auto max-w-[80%]">
                    <p className="text-sm text-gray-300">{data.responseSnippet}</p>
                </div>
            )}
        </div>
    </div>
);
const AnalysisDisplay = ({ data }) => ( /* ... code remains the same ... */
     <div className="border border-cyan-900/50 rounded-md p-4 bg-gray-900/30 h-full text-left">
         <div className="flex justify-between items-center mb-3 pb-2 border-b border-cyan-900/30">
             <span className="text-xs text-cyan-400 font-medium">ENGAGEMENT ANALYSIS</span>
             <Activity size={16} className="text-cyan-600"/>
         </div>
         <div className="space-y-2 text-sm">
             <p>Email Opened: <span className={`font-medium ${data.emailOpened ? 'text-green-400' : 'text-gray-500'}`}>{data.emailOpened ? 'Yes' : 'No'}</span></p>
             <p>Link Clicked: <span className={`font-medium ${data.linkClicked ? 'text-green-400' : 'text-gray-500'}`}>{data.linkClicked ? 'Yes' : 'No'}</span></p>
             <p>Sentiment Score: <span className="font-medium text-cyan-300">{data.sentiment}%</span></p>
             <p>Lead Score: <span className="font-medium text-cyan-300">{data.leadScore}/100</span></p>
         </div>
    </div>
);
const QualificationDisplay = ({ data }) => ( /* ... code remains the same ... */
     <div className="border border-cyan-900/50 rounded-md p-4 bg-gray-900/30 h-full text-left">
         <div className="flex justify-between items-center mb-3 pb-2 border-b border-cyan-900/30">
             <span className="text-xs text-cyan-400 font-medium">LEAD QUALIFICATION</span>
             <CheckCircle size={16} className="text-cyan-600"/>
         </div>
         <div className="space-y-2 text-sm">
             {Object.entries(data.criteria).map(([key, value]) => (
                 <p key={key} className="flex items-center">
                    <span className="w-20 text-gray-500 capitalize">{key}:</span>
                    <span className={`font-medium ${value ? 'text-green-400' : 'text-yellow-400'}`}>
                        {value ? 'Confirmed' : 'Pending'}
                    </span>
                     {value && <CheckCircle size={14} className="ml-2 text-green-500"/>}
                 </p>
             ))}
         </div>
         <p className="mt-3 text-xs text-cyan-300 font-semibold">Status: {data.status}</p>
    </div>
);
const MeetingDisplay = ({ data }) => (
    <div className="border border-cyan-900/50 rounded-md p-4 bg-gray-900/30 h-full text-left">
         <div className="flex justify-between items-center mb-3 pb-2 border-b border-cyan-900/30">
             <span className="text-xs text-cyan-400 font-medium">MEETING CONFIRMED</span>
             <CalendarCheck size={16} className="text-green-500"/>
         </div>
         <div className="space-y-1 text-sm">
             <p>Prospect: <span className="text-gray-300">{data.prospect}</span></p>
             <p>Date: <span className="text-gray-300">{data.date}</span></p>
             <p>Time: <span className="text-gray-300">{data.time}</span></p>
             <p>Platform: <span className="text-gray-300">{data.platform}</span></p>
             {/* CRM Record Link - Redirect to Calendly */}
             <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 hover:underline mt-2 inline-block">View CRM Record (Book Demo)</a>
         </div>
    </div>
);

const DetailedWorkflowVisualization = () => {
    const steps = [
        { id: 1, name: "Prospect ID", icon: Users, type: 'analysis', data: { emailOpened: false, linkClicked: false, sentiment: 60, leadScore: 45, description: "Identified high-value prospect: Sarah Chen (CTO @ Globex Inc.) based on ICP match." } },
        { id: 2, name: "Cold Email", icon: Mail, type: 'email', data: { to: "sarah.chen@globex.com", subject: "AI-driven efficiency for Globex", bodySnippet: "Hi Sarah, noticed Globex's focus on innovation. Our AI SDR, Anne, could significantly boost your team's outreach effectiveness..." } },
        { id: 3, name: "Track", icon: Activity, type: 'analysis', data: { emailOpened: true, linkClicked: false, sentiment: 65, leadScore: 55, description: "Email opened, interest detected. No reply yet. Sentiment slightly positive." } },
        { id: 4, name: "LinkedIn DM", icon: Linkedin, type: 'linkedin', data: { to: "Sarah Chen", messageSnippet: "Hi Sarah, following up on my email. Anne AI helps teams like yours automate SDR tasks effectively. Worth a quick chat?", responseSnippet: null } },
        { id: 5, name: "Qualify", icon: CheckCircle, type: 'qualification', data: { criteria: { need: true, budget: false, timeline: false }, status: "Interest Shown, Info Pending" } },
        { id: 6, name: "Response", icon: MessageSquare, type: 'linkedin', data: { to: "Sarah Chen", messageSnippet: "...", responseSnippet: "Interesting concept. Can you share more details? Perhaps a brief call next week?" } },
        { id: 7, name: "Meeting Booked", icon: CalendarCheck, type: 'meeting', data: { prospect: "Sarah Chen", date: "Oct 28, 2025", time: "11:00 AM PST", platform: "Google Meet", description: "Meeting confirmed via Anne's scheduling AI. CRM updated." } },
    ];
    const [currentStep, setCurrentStep] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const contentRef = useRef(null); // Ref for content animation

    // Auto-play logic (no changes needed here)
    useEffect(() => {
        let timer;
        if (isPlaying && currentStep < steps.length - 1) {
            timer = setTimeout(() => {
                 if(contentRef.current) contentRef.current.classList.add('content-exit'); // Start exit animation
                 setTimeout(() => {
                     setCurrentStep(prev => prev + 1);
                     if(contentRef.current) {
                        contentRef.current.classList.remove('content-exit');
                        contentRef.current.classList.add('content-enter'); // Start enter animation
                        setTimeout(() => contentRef.current.classList.remove('content-enter'), 300); // Clean up enter class
                     }
                 }, 300); // Wait for exit animation
            }, 2500); // Auto-advance every 2.5 seconds
        } else if (currentStep === steps.length - 1 && isPlaying) {
              timer = setTimeout(() => { // Loop after pause
                   if(contentRef.current) contentRef.current.classList.add('content-exit');
                    setTimeout(() => {
                        setCurrentStep(0);
                         if(contentRef.current) {
                            contentRef.current.classList.remove('content-exit');
                            contentRef.current.classList.add('content-enter');
                            setTimeout(() => contentRef.current.classList.remove('content-enter'), 300);
                         }
                    }, 300);
               }, 4000);
        }
        return () => clearTimeout(timer);
    }, [currentStep, isPlaying, steps.length]);

    // Keep internal step navigation
    const handleStepClick = (index) => {
        if (index === currentStep) return; // No change if clicking current step
        setIsPlaying(false); // Pause on manual click
        if(contentRef.current) contentRef.current.classList.add('content-exit');
        setTimeout(() => {
            setCurrentStep(index);
             if(contentRef.current) {
                contentRef.current.classList.remove('content-exit');
                contentRef.current.classList.add('content-enter');
                setTimeout(() => contentRef.current.classList.remove('content-enter'), 300);
             }
        }, 300);
    }

    // Keep internal play/pause toggle
    const togglePlay = () => {
        if (!isPlaying && currentStep === steps.length - 1) {
            if(contentRef.current) contentRef.current.classList.add('content-exit');
            setTimeout(() => {
                 setCurrentStep(0);
                 if(contentRef.current) {
                    contentRef.current.classList.remove('content-exit');
                    contentRef.current.classList.add('content-enter');
                    setTimeout(() => contentRef.current.classList.remove('content-enter'), 300);
                 }
            }, 300);
        }
       setIsPlaying(prev => !prev);
    }

    // Helper for keyboard activation - Keep internal functionality
    const handleKeyDown = (event, action, index = null) => {
        if (event.key === 'Enter' || event.key === ' ') {
            if (action === 'togglePlay') {
                togglePlay();
            } else if (action === 'stepClick' && index !== null) {
                handleStepClick(index);
            }
        }
    };

     const currentStepData = steps[currentStep];
     const renderContent = () => {
        if (!currentStepData) return <p>Loading...</p>;
        switch (currentStepData.type) {
            case 'email': return <EmailDisplay data={currentStepData.data} />;
            case 'linkedin': return <LinkedInDisplay data={currentStepData.data} />;
            case 'analysis': return <AnalysisDisplay data={currentStepData.data} />;
            case 'qualification': return <QualificationDisplay data={currentStepData.data} />;
            case 'meeting': return <MeetingDisplay data={currentStepData.data} />; // Updated to use new MeetingDisplay
            default: return <p className="text-gray-500">Initializing workflow...</p>;
        }
    };


    return (
        <div className="bg-black/60 border border-cyan-900/60 p-6 rounded-lg relative overflow-hidden group hover:border-cyan-600/70 transition-all duration-300 shadow-xl shadow-cyan-900/15 backdrop-blur-md h-full flex flex-col isolate">
             {/* Background animation element */}
            <div className="absolute inset-0 -z-10 overflow-hidden">
                <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-gradient-radial from-cyan-700/15 via-transparent to-transparent animate-pulse-slow opacity-50"></div>
                <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-radial from-blue-700/15 via-transparent to-transparent animate-pulse-slow-delay-2 opacity-50"></div>
            </div>

            <div className="flex items-center justify-between mb-5 flex-shrink-0 pb-3 border-b border-cyan-900/30">
                <h3 className="text-xl text-cyan-300 flex items-center font-medium tracking-wide">
                    <Zap className="mr-2.5" size={22} />
                    Autonomous Workflow Engine
                </h3>
                 {/* Play/Pause div button - Keep internal functionality */}
                 <div
                    role="button"
                    tabIndex={0}
                    onClick={togglePlay}
                    onKeyDown={(e) => handleKeyDown(e, 'togglePlay')}
                    title={isPlaying ? "Pause" : "Play"}
                    aria-label={isPlaying ? "Pause workflow animation" : "Play workflow animation"}
                    className="text-cyan-400 hover:text-cyan-200 transition-colors p-1 rounded-full hover:bg-cyan-800/50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-opacity-50"
                 >
                    {isPlaying ? <Clock size={20} className="animate-spin-slow opacity-80"/> : <ArrowRight size={20}/>}
                 </div>
            </div>

            {/* Steps Visualization (Using div for buttons) - Keep internal functionality */}
            <div className="flex items-center justify-between mb-6 space-x-1 md:space-x-2 overflow-x-auto pb-2 flex-shrink-0">
                {steps.map((step, index) => (
                    <React.Fragment key={step.id}>
                         <div
                             role="button"
                             tabIndex={0}
                             onClick={() => handleStepClick(index)}
                             onKeyDown={(e) => handleKeyDown(e, 'stepClick', index)}
                             aria-label={`Go to step ${index + 1}: ${step.name}`}
                             className={`flex flex-col items-center cursor-pointer flex-shrink-0 w-14 group/step transition-opacity duration-300 ${index <= currentStep ? 'opacity-100' : 'opacity-30 hover:opacity-60'} focus:outline-none focus:ring-1 focus:ring-cyan-400 rounded-full`}
                             title={step.name}
                         >
                            <div className={`p-2.5 rounded-full border-2 transition-all duration-500 relative ${index === currentStep ? 'border-cyan-400 bg-cyan-800/40 scale-110 shadow-md shadow-cyan-400/30' : 'border-gray-700 group-hover/step:border-cyan-700 bg-gray-800/50'}`}>
                                <step.icon size={18} className={`transition-colors ${index <= currentStep ? 'text-cyan-300' : 'text-gray-500 group-hover/step:text-cyan-500'}`}/>
                                {index === currentStep && <div className="absolute inset-0 rounded-full border border-cyan-500 animate-ping opacity-70"></div>}
                            </div>
                            <span className={`mt-1.5 text-[10px] font-mono text-center transition-colors whitespace-nowrap ${index === currentStep ? 'text-cyan-300 font-semibold' : 'text-gray-500 group-hover/step:text-cyan-400'}`}>{step.name}</span>
                         </div>
                        {index < steps.length - 1 && (
                           <div className={`flex-grow h-0.5 transition-all duration-500 min-w-[10px] md:min-w-[20px] ${index < currentStep ? 'bg-gradient-to-r from-cyan-500 to-cyan-700' : 'bg-gray-700'}`}></div>
                        )}
                    </React.Fragment>
                ))}
            </div>

            {/* Current Step Content Display */}
            <div ref={contentRef} className="flex-grow bg-black/30 border border-cyan-900/40 rounded-lg p-1 min-h-[180px] relative overflow-hidden">
                {/* Content is rendered inside */}
                <div className="h-full w-full p-3 custom-scrollbar overflow-y-auto">
                     {renderContent()}
                </div>
                 {/* Inner glow/shader effect */}
                <div className="absolute inset-0 pointer-events-none rounded-lg border border-cyan-700/20 shadow-[inset_0_0_15px_rgba(6,182,212,0.1)]"></div>
            </div>

            {/* Timeline/Progress */}
            <div className="w-full bg-gray-800/70 rounded-full h-2 mt-5 flex-shrink-0 overflow-hidden border border-cyan-900/30">
                 <div
                    className="bg-gradient-to-r from-cyan-500 to-blue-500 h-full rounded-full transition-all duration-500 ease-linear relative"
                    style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                 >
                    <div className="absolute right-0 top-0 bottom-0 w-4 bg-white/30 animate-pulse blur-sm"></div> {/* Pulse effect */}
                 </div>
            </div>
        </div>
    );
};


// --- Technology Showcase Section (Uses updated chart) ---
const TechnologyShowcase = () => {
  return (
    <section id="technology" className="py-24 bg-black/20 border-y border-cyan-900/30 px-4 isolate">
       <div className="container mx-auto">
        <div className="text-center mb-16">
          <div className="uppercase text-xs tracking-wider text-cyan-400 mb-2 font-semibold">Under the Hood</div>
          <h2 className="text-4xl lg:text-5xl font-light mb-4 text-white">The Architecture of Anne</h2>
          <p className="text-gray-400 max-w-xl mx-auto text-lg">
            Explore the sophisticated systems powering Anne's autonomous capabilities and human-like interaction.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <TerminalDemo />
            <HolographicStat />
        </div>
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            <NeuralNetworkAnimation />
            <MarketAnalysisPanel />
            <VectorDatabaseMonitor />
         </div>
         <div className="grid grid-cols-1 xl:grid-cols-5 gap-8"> {/* Adjusted grid for layout */}
             <div className="xl:col-span-2"> {/* Chart takes 2/5 */}
                 <RealTimeChartSimulation /> {/* Using the enhanced chart */}
             </div>
             <div className="xl:col-span-3"> {/* Workflow takes 3/5 */}
                 <DetailedWorkflowVisualization />
             </div>
         </div>
      </div>
    </section>
  );
};

// --- CTA Section Component (Uses div for button) ---
const CTASection = () => {
    // External Link Handler
    const redirectToCalendly = () => {
        window.location.href = CALENDLY_URL;
    };

    // Keyboard handler for external link
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            redirectToCalendly();
        }
    };

  return (
    <section id="cta" className="py-24 px-4 relative isolate">
      <div className="container mx-auto">
        <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-cyan-900/10 to-transparent -z-10"></div>
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-cyan-900/10 to-transparent -z-10"></div>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light mb-6 text-white leading-tight">
            Ready To Deploy The Future <br/> of Sales Development?
          </h2>
          <div className="w-20 h-1 bg-cyan-400 mx-auto mb-8"></div>
          <p className="text-gray-400 mb-8 text-lg max-w-xl mx-auto">
            Integrate Anne into your sales process and witness a paradigm shift in outreach efficiency and effectiveness. Request a personalized demonstration today.
          </p>

          {/* --- Pricing Info --- */}
          <div className="mb-10">
            <p className="text-cyan-400 text-lg font-medium flex items-center justify-center">
                <DollarSign size={20} className="mr-2 text-cyan-500"/> Deployment starts at <span className="text-white font-semibold mx-1.5">$5</span> per hour
            </p>
            <p className="text-xs text-gray-500 mt-1">(Usage-based pricing scales with your needs)</p>
          </div>
          {/* --- End Pricing Info --- */}

           {/* CTA div button - Redirect to Calendly */}
          <div
            role="button"
            tabIndex={0}
            onClick={redirectToCalendly}
            onKeyDown={handleKeyDown}
            className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white py-4 px-10 rounded-md mx-auto flex items-center justify-center text-lg font-semibold shadow-lg shadow-cyan-500/30 transition duration-300 transform hover:scale-105 cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 focus:ring-offset-black w-fit" // Added w-fit for centering
           >
            REQUEST LIVE DEMO
            <ChevronRight size={20} className="ml-2" />
          </div>
        </div>
      </div>
    </section>
  );
};


// --- Footer Component (Uses <a> tags) ---
const Footer = () => {
    // Keyboard handler for internal scroll
     const handleKeyDownInternal = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
             scrollToSection('home');
        }
    };

    return (
        <footer className="bg-black border-t border-cyan-900/40 px-4 py-12 isolate">
             <div className="container mx-auto">
                 <div className="flex flex-col md:flex-row justify-between items-center">
                     {/* Logo - Keep internal scroll */}
                     <div className="flex items-center mb-6 md:mb-0 cursor-pointer" onClick={() => scrollToSection('home')} tabIndex="0" onKeyDown={handleKeyDownInternal}>
                         <div className="text-cyan-400 font-bold text-2xl tracking-wider mr-2">ANNE</div>
                         <div className="text-xs bg-cyan-900/40 px-2 py-1 rounded-sm text-cyan-300">AI SDR</div>
                     </div>
                     {/* Footer Links - Redirect to Calendly */}
                     <div className="flex flex-wrap justify-center space-x-6 md:space-x-8 text-sm text-gray-500 mb-6 md:mb-0">
                         <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition">Privacy Policy</a>
                         <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition">Terms of Service</a>
                         <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition">Documentation</a>
                         <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition">Contact Support</a>
                     </div>
                     {/* Social Links - Redirect to Calendly */}
                     <div className="flex space-x-5 text-gray-600">
                         <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn (Book Demo)" className="hover:text-cyan-400 transition"><Linkedin size={18}/></a>
                         <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" aria-label="Twitter (Book Demo)" className="hover:text-cyan-400 transition"><Twitter size={18}/></a>
                     </div>
                </div>
                <div className="h-px bg-cyan-900/30 my-8"></div>
                <div className="text-center text-gray-600 text-xs"> © {new Date().getFullYear()} Efoundry. All rights reserved. Anne AI SDR is a product of Efoundry Labs. <br /> Designed with the future in mind. </div>
            </div>
        </footer>
    );
 };


// --- Background Effects Component (No changes needed here) ---
const BackgroundEffects = ({ scanEffect }) => {
  return (
    <>
      {/* (Previous background elements remain the same) */}
      <div className={`fixed inset-0 pointer-events-none transition-opacity duration-1000 z-0 ${scanEffect ? 'opacity-30' : 'opacity-0'}`}> <div className="h-full w-full relative"> <div className="absolute top-0 h-1 w-full bg-gradient-to-r from-transparent via-cyan-400/80 to-transparent animate-scan"></div> </div> </div>
      <div className="fixed inset-0 bg-grid-pattern opacity-[0.03] pointer-events-none -z-10"></div>
      <div className="fixed inset-0 bg-noise-pattern opacity-[0.04] pointer-events-none -z-10"></div>
      <div className="fixed top-0 left-0 w-1/3 h-1/3 bg-gradient-to-br from-cyan-800/20 to-transparent blur-3xl -z-10 opacity-50"></div>
      <div className="fixed bottom-0 right-0 w-1/3 h-1/3 bg-gradient-to-tl from-blue-800/20 to-transparent blur-3xl -z-10 opacity-50"></div>

      <style jsx global>{`
        /* (Previous global styles remain the same) */
        html { scroll-behavior: smooth; }
        body { scrollbar-width: thin; scrollbar-color: #0891b2 #0f172a; }
        ::-webkit-scrollbar { width: 8px; } ::-webkit-scrollbar-track { background: #0f172a; } ::-webkit-scrollbar-thumb { background-color: #0891b2; border-radius: 4px; border: 2px solid #0f172a; }
         .custom-scrollbar { scrollbar-width: thin; scrollbar-color: #0e7490 #1e293b; }
         .custom-scrollbar::-webkit-scrollbar { width: 6px; } .custom-scrollbar::-webkit-scrollbar-track { background: #1e293b; border-radius: 3px; } .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #0e7490; border-radius: 3px; }
        @keyframes scan { 0% { transform: translateY(-10px); opacity: 0; } 50% { opacity: 0.7; } 100% { transform: translateY(100vh); opacity: 0; } } .animate-scan { animation: scan 4s linear infinite; }
        .bg-grid-pattern { background-image: linear-gradient(to right, rgba(4, 179, 206, 0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(4, 179, 206, 0.1) 1px, transparent 1px); background-size: 30px 30px; }
        .bg-noise-pattern { background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 250 250' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E"); }
        .glitch-text { position: relative; text-shadow: 0 0 3px rgba(4, 179, 206, 0.5); animation: glitch 3s linear infinite alternate-reverse; }
        @keyframes glitch { 0% { text-shadow: 1px 0 0 rgba(0, 255, 255, 0.5), -1px 0 0 rgba(255, 0, 0, 0.5); } 2% { text-shadow: 1px 0 0 rgba(0, 255, 255, 0.5), -1px 0 0 rgba(255, 0, 0, 0.5); transform: translate(0); } 3% { text-shadow: -1px 0 0 rgba(0, 255, 255, 0.5), 1px 0 0 rgba(255, 0, 0, 0.5); transform: translate(-1px, 1px); } 4% { text-shadow: 1px 0 0 rgba(0, 255, 255, 0.5), -1px 0 0 rgba(255, 0, 0, 0.5); transform: translate(0); } 30% { text-shadow: 1px 0 0 rgba(0, 255, 255, 0.5), -1px 0 0 rgba(255, 0, 0, 0.5); transform: translate(0); } 31% { text-shadow: 0 0 3px rgba(4, 179, 206, 0.5); transform: translate(1px, -1px) skewX(-5deg); } 32% { text-shadow: 0 0 3px rgba(4, 179, 206, 0.5); transform: translate(0); } 100% { text-shadow: 0 0 3px rgba(4, 179, 206, 0.5); } }
        .glow-effect { filter: drop-shadow(0 0 12px rgba(4, 179, 206, 0.5)); }
        @keyframes pulse-slow { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } } .animate-pulse-slow { animation: pulse-slow 3s cubic-bezier(0.4, 0, 0.6, 1) infinite; } .animate-pulse-slow-delay-1 { animation: pulse-slow 3s cubic-bezier(0.4, 0, 0.6, 1) infinite; animation-delay: 0.5s; } .animate-pulse-slow-delay-2 { animation: pulse-slow 3s cubic-bezier(0.4, 0, 0.6, 1) infinite; animation-delay: 1s; } .animate-pulse-slow-delay-3 { animation: pulse-slow 3s cubic-bezier(0.4, 0, 0.6, 1) infinite; animation-delay: 1.5s; }
        @keyframes neural-flow { 0% { stroke-dashoffset: 10; } 100% { stroke-dashoffset: 0; } } .neural-line { stroke-dasharray: 5 5; animation: neural-flow 1s linear infinite; } .animate-neural-1 { animation-duration: 1.5s; } .animate-neural-2 { animation-duration: 1.2s; animation-direction: reverse;} .animate-neural-3 { animation-duration: 1.8s; }
        @keyframes face-scan { 0% { transform: translateY(-100%); } 100% { transform: translateY(100%); } } .animate-face-scan { animation: face-scan 1.5s linear infinite; }
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .animate-spin-slow { animation: spin-slow 15s linear infinite; }
        .isolate { isolation: isolate; }

        /* Workflow Content Transition */
        @keyframes contentFadeOut { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(10px); } }
        @keyframes contentFadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }

        .content-exit { animation: contentFadeOut 0.3s ease-out forwards; }
        .content-enter { animation: contentFadeIn 0.3s ease-in forwards; }

         /* Line clamp utility */
        .line-clamp-3 { overflow: hidden; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 3; }

        /* Focus visible styles for div buttons */
        [role="button"]:focus-visible {
             outline: 2px solid #22d3ee; /* Cyan outline */
             outline-offset: 2px;
        }
        /* More specific overrides if needed */
        .header-nav-button:focus-visible { outline-color: #67e8f9; }
        .cta-button:focus-visible { outline-color: #a5f3fc; }

         /* Focus visible styles for links */
        a:focus-visible {
            outline: 2px solid #22d3ee; /* Cyan outline */
            outline-offset: 2px;
            border-radius: 2px; /* Optional: Adds a slight rounding */
        }


      `}</style>
    </>
  );
};


// --- Main Landing Page Component ---
export const EfoundrySDR = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [scanEffect, setScanEffect] = useState(false);
  const [mainPaddingTop, setMainPaddingTop] = useState(0); // Padding for main content below header

  // Effect to add scan effect on mount
  useEffect(() => {
      setScanEffect(true);
      const timer = setTimeout(() => setScanEffect(false), 1500);
      return () => clearTimeout(timer);
  }, []);


  // Effect to calculate header height for main content padding
  useEffect(() => {
      const headerElement = document.querySelector('header');
      const calculatePadding = () => {
          setMainPaddingTop(headerElement?.offsetHeight || 0);
      };

      calculatePadding(); // Initial calculation
      const resizeObserver = new ResizeObserver(calculatePadding);
      if (headerElement) {
          resizeObserver.observe(headerElement);
      }
      window.addEventListener('resize', calculatePadding); // Recalculate on resize

      return () => {
          if (headerElement) resizeObserver.unobserve(headerElement);
          window.removeEventListener('resize', calculatePadding);
      };
  }, []);

  // Effect for intersection observer
  useEffect(() => {
      // Use the calculated mainPaddingTop (which is header height)
      const headerHeight = mainPaddingTop; // Use state value
      const bannerElement = document.getElementById('announcement-banner');
      const bannerHeight = bannerElement?.offsetHeight || 0;
      const totalOffset = headerHeight + bannerHeight;

      const rootMarginTop = -(totalOffset + 10); // Negative offset + buffer
      const rootMarginBottom = -100; // Adjust as needed

      const sections = ['home', 'features', 'technology', 'cta'];
      const observer = new IntersectionObserver( (entries) => {
          entries.forEach(entry => {
              // Prioritize entry closest to the top that is intersecting sufficiently
              if (entry.isIntersecting && entry.boundingClientRect.top < window.innerHeight * 0.5) {
                  // Check if it's reasonably visible (e.g., >= 20% intersection)
                  if (entry.intersectionRatio >= 0.2) {
                     setActiveSection(entry.target.id);
                  }
              }
              // Fallback: If no section is actively intersecting near the top,
              // check for any intersecting section (might be lower on the page)
              else if (entry.isIntersecting && entry.intersectionRatio >= 0.1) {
                   // Only update if the current active section is NOT intersecting
                   const currentActiveElement = document.getElementById(activeSection);
                   if (!currentActiveElement || !observer || !observer.takeRecords) { // Check if observer is ready
                        setActiveSection(entry.target.id); // Initial or safe update
                   } else {
                       const activeEntry = observer.takeRecords().find(e => e.target.id === activeSection);
                       if (!activeEntry || !activeEntry.isIntersecting) {
                           setActiveSection(entry.target.id);
                       }
                   }
               }
          });
       }, {
           threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7], // More thresholds for better detection
           rootMargin: `${rootMarginTop}px 0px ${rootMarginBottom}px 0px` // Dynamic root margin based on header+banner
       }
      );

      sections.forEach(id => {
          const element = document.getElementById(id);
          if (element) observer.observe(element);
      });

      return () => {
          sections.forEach(id => {
              const element = document.getElementById(id);
              if (element) observer.unobserve(element);
          });
      };
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [mainPaddingTop, activeSection]); // Rerun observer setup if header height changes or active section changes


  return (
    <div className="min-h-screen bg-black text-gray-200 font-sans relative overflow-x-hidden">
      <BackgroundEffects scanEffect={scanEffect} />
      {/* Header is rendered first, fixed */}
      <Header activeSection={activeSection} />
      {/* Banner is rendered next, fixed below header */}
      <AnnouncementBanner />
      {/* Apply dynamic padding top to main content area */}
      <main style={{ paddingTop: `${mainPaddingTop}px` }}>
        <Hero />
        <FeaturesSection />
        <TechnologyShowcase />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default EfoundrySDR;